import { useMemo } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Wallet,
  BookOpen,
  UserCog,
  LogOut,
  Archive,
  Settings,
} from "lucide-react";

const navItems = [
  { to: "/", label: "Inicio", icon: LayoutDashboard, testId: "nav-inicio", end: true },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testId: "nav-agenda" },
  { to: "/pacientes", label: "Pacientes", icon: Users, testId: "nav-pacientes" },
  { to: "/historial", label: "Historial", icon: Archive, testId: "nav-historial" },
  { to: "/pagos", label: "Pagos", icon: Wallet, testId: "nav-pagos" },
  { to: "/caja", label: "Libro de Caja", icon: BookOpen, testId: "nav-caja" },
  { to: "/profesionales", label: "Profesionales", icon: UserCog, testId: "nav-profesionales", adminOnly: true },
  { to: "/ajustes", label: "Ajustes", icon: Settings, testId: "nav-ajustes", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const cfg = useConfig();
  const navigate = useNavigate();

  const visibleNav = useMemo(
    () => navItems.filter((i) => !i.adminOnly || user?.role === "admin"),
    [user?.role]
  );

  const roleLabel = {
    admin: "Administrador",
    profesional: "Profesional",
    recepcionista: "Recepción",
  }[user?.role] || "";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="grain min-h-screen flex bg-[hsl(var(--background))]">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-neutral-200 bg-white/60 backdrop-blur-md">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-neutral-200/70">
          <img
            src="/logo-mark.png"
            alt={cfg.profesional_nombre}
            className="w-10 h-10 object-contain"
          />
          <div className="leading-tight">
            <div className="font-bold text-neutral-900 tracking-tight">{cfg.profesional_nombre}</div>
            <div className="overline text-neutral-500">{cfg.consultorio_nombre}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNav.map(({ to, label, icon: Icon, testId, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                <span>{label}</span>
              </NavLink>
            ))}
        </nav>
        <div className="p-4 border-t border-neutral-200/70">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
              style={{ backgroundColor: user?.color || "#6B8E6B" }}
            >
              {(user?.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-neutral-900 truncate">{user?.name}</div>
              <div className="text-xs text-neutral-500">{roleLabel}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="btn-ghost-pill w-full justify-start text-neutral-600"
          >
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 glass-header px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo-mark.png" alt={cfg.profesional_nombre} className="w-8 h-8 object-contain" />
          <span className="font-bold text-neutral-900 tracking-tight">{cfg.profesional_nombre}</span>
        </div>
        <button data-testid="logout-mobile-btn" onClick={handleLogout} className="btn-ghost-pill text-sm">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 relative z-10 md:pt-0 pt-14">
        <Outlet />
        {/* Mobile bottom tabs */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 flex justify-around py-2">
          {visibleNav
            .slice(0, 5)
            .map(({ to, label, icon: Icon, testId, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`${testId}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-2 py-1 text-[11px] ${
                    isActive ? "text-[hsl(var(--sage-hover))]" : "text-neutral-500"
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            ))}
        </nav>
      </main>
    </div>
  );
}
